import { ColumnVO, IServerSideGetRowsRequest } from '@ag-grid-community/core';
import differenceWith from 'lodash/differenceWith';
import each from 'lodash/each';
import find from 'lodash/find';
import findKey from 'lodash/findKey';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import replace from 'lodash/replace';

import { composeQuery, select, f, and, orderBy, prerenderedSelect } from 'common/soql_builder';
import { NamedSelect } from 'common/soql_builder/select';
import { Expr } from 'common/types/soql';
import { ViewColumn } from 'common/types/viewColumn';

import { ColumnAggregation } from '../dataProviders/types';
import { CustomSortConfig, HierarchyColumnConfig, OrderConfig } from '../vif';
import { GROUP_COLUMN_PREFIX, NULL_GROUPING } from '../views/agGridReact/Constants';

import { isPivotInAgRequest } from './PivotHelpers';
import { selectAlias } from './SoqlAliasHelpers';
import { countAlias, measureAlias } from './SoqlHelpers';
import { findCorrespondingColumn } from './TableDataColumnHelpers';
import { buildCasesParts, removeNullGroupKeys } from './TableDataHelpers';
import { isCustomSortEnabled, isTableAdvancedTotalingEnabled } from './VifSelectors';
import { BACKTICK_WRAPPED_TEXT } from 'common/authoring_workflow/components/modals/AdvancedTotalingModal/helpers/formulaHelpers';

function generateWhereQuery(
  columns: ViewColumn[],
  groupKeys: (string | null)[],
  rowGroupCols: ColumnVO[],
): Expr | undefined {
  const whereParts: Expr[] = [];

  if (groupKeys) {
    groupKeys.forEach(function (key, i) {
      const whereColumn = findCorrespondingColumn(columns, rowGroupCols[i]);
      if (typeof whereColumn !== 'undefined') {
        if (key === null) {
          whereParts.push(f.isNull(whereColumn));
        } else {
          whereParts.push(f.eq(whereColumn, key));
        }
      }
    });
  }

  if (whereParts.length > 1) {
    return and(...whereParts);
  }

  return !isEmpty(whereParts) ? whereParts[0] : undefined;
}

export function isDoingGrouping(rowGroupCols: ColumnVO[], groupKeys: (string | null)[]) {
  // We do not want to group at the lowest level
  return rowGroupCols?.length > groupKeys?.length;
}

/**
 *
 * @param {ViewColumn []} columns - All of the columns being used in the table visualization.
 * @param {string} legacyWhereClause - The output of SoqlHelpers.whereClauseFilteringOwnColumn
 * @param {Object} agRequest - The data request information from Ag Grid.
 * @param {string} searchString - An optional search string used on the Primer
 * @returns - a query to be used in the Soql Query Builder, invokeSoql function.
 */
export function tableRowCountQuery(
  columns: ViewColumn[],
  legacyWhereClause: string,
  agRequest: IServerSideGetRowsRequest,
  searchString?: string,
) {
  const { groupKeys: agGroupKeys, rowGroupCols } = agRequest;
  const groupKeys = agGroupKeys.map((key) => (key === NULL_GROUPING || key == undefined ? null : key));

  const countSelectQuery = () => {
    let selectQueries: NamedSelect[] = [];
    if (isDoingGrouping(rowGroupCols, groupKeys)) {
      const selectCol = rowGroupCols[groupKeys.length];

      const colId = findCorrespondingColumn(columns, selectCol);
      if (typeof colId !== 'undefined') {
        selectQueries = [select(f.countDistinct(colId), countAlias())];
      }
    } else {
      selectQueries = [select(f.countStar(), countAlias())];
    }

    return selectQueries;
  };

  const rowCountQuery = composeQuery({
    selects: countSelectQuery(),
    where: generateWhereQuery(columns, groupKeys, rowGroupCols),
    search: searchString,
    legacyWhereClause,
  });

  return rowCountQuery;
}

/**
 * Used to determine if the grouping column contains a null value
 * @param {ViewColumn []} columns - All of the columns being used in the table visualization.
 * @param {string} legacyWhereClause - The output of SoqlHelpers.whereClauseFilteringOwnColumn
 * @param {Object} agRequest - The data request information from Ag Grid.
 * @returns - a query to be used in the Soql Query Builder, invokeSoql function.
 */
export function tableNullGroupingCountQuery(
  columns: ViewColumn[],
  legacyWhereClause: string,
  agRequest: IServerSideGetRowsRequest,
) {
  const { rowGroupCols, groupKeys: agGroupKeys } = agRequest;
  const groupKeys = agGroupKeys.map((key) => (key === NULL_GROUPING || key == undefined ? null : key));

  // if we're grouping, we need to check if the grouping column contains a null value;
  // if so, countDistinct will have ignored it and the resulting row count will be off by one
  // the below where clause will use the group keys as usual, but will also add a final
  // `<column> IS NULL`, <column> being the last opened column (or the top level unopened column)
  return composeQuery({
    selects: [select(f.countStar(), countAlias())],
    where: generateWhereQuery(columns, [...groupKeys, null], rowGroupCols),
    legacyWhereClause,
  });
}

// The purpose of this function is to create a map of column names to safe aliases for use in queries.
// We want to have a safe alias for each column, since SoqlBuilder requires an alias, and the field name
// may be a reserved Soql word (e.g. `group`).
export function createSelectAliasMap(
  agRequest: IServerSideGetRowsRequest,
  columns: ViewColumn[],
): { [key: string]: string } {
  const { rowGroupCols, groupKeys, pivotCols } = agRequest;
  const isPivoting = isPivotInAgRequest(agRequest);
  const aliasMap: { [key: string]: string } = {};
  let aliasCount = 0;

  if (isPivoting) {
    pivotCols.forEach((pivotCol) => {
      aliasMap[pivotCol.id] = selectAlias(aliasCount ? aliasCount : undefined);
      aliasCount += 1;
    });
  }

  // if we are making a grouping query, it's of the form SELECT a, AGG(b), AGG(c) ... WHERE ... GROUP BY a;
  if (isDoingGrouping(rowGroupCols, groupKeys)) {
    const selectCol = rowGroupCols[groupKeys.length];
    aliasMap[selectCol.id] = selectAlias(aliasCount);
  }

  if (!isEmpty(aliasMap)) {
    // We must have pivoted or grouped, so we early return
    return aliasMap;
  }

  // if we aren't grouping, we're at the lowest level, and all non-grouped columns will be part of the select
  // e.g. SELECT a, b, c, ... WHERE ...;
  const ungroupedColumns = differenceWith(columns, rowGroupCols, (c, r) => c.fieldName === r.id);
  return ungroupedColumns
    .map((col, idx) => [col.fieldName, selectAlias(idx)])
    .reduce((aliases, [colId, alias]) => ({ ...aliases, [colId]: alias }), {});
}

export function getSelectAggregationsQuery(
  valueCols: any[],
  columns: ViewColumn[],
  nonStandardAggregations?: ColumnAggregation[] | null,
): NamedSelect[] {
  const selectQueries: NamedSelect[] = [];
  const aggregations = valueCols.filter((col) => !!col.aggFunc);

  if (aggregations.length) {
    const aggregationSelects = aggregations
      .map(({ id, aggFunc }, idx) => {
        // @ts-expect-error implicit any index
        if (typeof aggFunc !== 'undefined' && typeof f[aggFunc] === 'function') {
          const viewCol = findCorrespondingColumn(columns, { id });

          // Postgresql does not allow for min or max of boolean columns,
          // so we need to use this work around.
          if (viewCol?.dataTypeName === 'checkbox' && (aggFunc === 'max' || aggFunc === 'min')) {
            // @ts-expect-error implicit any index
            return select(f.eq(f[aggFunc](f.iif(viewCol, 1, 0)), 1), measureAlias(idx));
          } else if (viewCol?.dataTypeName === 'url' && (aggFunc === 'max' || aggFunc === 'min')) {
            // @ts-expect-error implicit any index
            return select(f.url(f[aggFunc](f.url_url(viewCol)), null), measureAlias(idx));
          }

          // @ts-expect-error implicit any index
          return select(f[aggFunc](viewCol), measureAlias(idx));
        } else if (nonStandardAggregations) {
          // in the case of a non-standard aggregation, we need to use the nonStandardAggregations
          // list to find the correct SoQL expression for this column/aggregationType
          const aggregationDefinition = nonStandardAggregations.find(
            (agg) => agg.fieldName === id && agg.aggregationType === aggFunc,
          );
          if (aggregationDefinition) {
            return prerenderedSelect(aggregationDefinition.soqlExpression, measureAlias(idx));
          }
        }
      })
      .filter((selectConfig): selectConfig is NamedSelect => !!selectConfig);
    selectQueries.push(...aggregationSelects);
  }
  return selectQueries;
}

export function createMeasureAliasMap(agRequest: IServerSideGetRowsRequest): { [key: string]: string } {
  return agRequest.valueCols
    .filter((col) => !!col.aggFunc)
    .map((col, idx) => [col.id, measureAlias(idx)])
    .reduce((aliases, [colId, alias]) => {
      return { ...aliases, [alias]: colId };
    }, {});
}

export function unaliasRowData(data: any[], aliasMap: { [key: string]: string }): any[] {
  return Object.keys(aliasMap).length
    ? data.map((row) => {
        const unaliasedRow = {} as any;
        for (const colName in row) {
          if (aliasMap[colName]) {
            unaliasedRow[aliasMap[colName]] = row[colName];
          } else {
            unaliasedRow[colName] = row[colName];
          }
        }
        return unaliasedRow;
      })
    : data;
}

/**
 * Recursive Helper: Builds the nested logic tree of the advanced totaling SoQL SELECT clauses.
 */
export const buildNestedExpression = (
  configIdx: number,
  viewCol: ViewColumn,
  aggFunc: string,
  activeConfigs: HierarchyColumnConfig[],
  parentConditions: string[] = [],
): string => {
  const currentConfig = activeConfigs[configIdx];
  const formula = currentConfig.customFormula!;

  // Replace the backticked IDs in the custom formula (e.g., `USA` + `Canada`)
  return formula.replace(BACKTICK_WRAPPED_TEXT, (_, formulaIdValue) => {
    // Create condition: `column_name` = 'formulaIdValue'
    const currentCondition = `\`${currentConfig.columnName}\` = '${formulaIdValue}'`;
    const allConditions = [...parentConditions, currentCondition];

    // If there is another level below with a formula, recurse
    if (configIdx + 1 < activeConfigs.length) {
      return `(${buildNestedExpression(configIdx + 1, viewCol, aggFunc, activeConfigs, allConditions)})`;
    }

    // LEAF NODE: Construct the final CASE statement
    const conditionStr = allConditions.join(' AND ');
    const measureIdentifier = `\`${viewCol.fieldName}\``;

    // Type-specific handling for Checkboxes and URLs is not typically required inside
    // sum/math formulas, so we use standard identifiers.
    return `${aggFunc}(case(${conditionStr}, ${measureIdentifier}, true, 0))`;
  });
};

/**
 * Generates a list of SoQL SELECT clauses for table aggregations, utilizing a **Hybrid Strategy**
 * to support complex custom formulas ("Advanced Totaling") alongside standard aggregations.
 *
 * 1. **Path A (Advanced Totaling):**
 * - Triggered **ONLY** when `aggFunc === 'sum'` AND there are active `hierarchyConfigs` starting from `startLevelIndex`.
 * - Uses recursive logic to parse custom formulas (e.g., `` `US` + `CA` ``).
 * - Replaces formula tokens with nested `CASE` statements that enforce hierarchy logic down to the leaf node.
 * - Generates raw SoQL strings to handle arithmetic ( `+`, `-`, `*`, `/`) defined in the formula.
 *
 * 2. **Path B (Standard Fallback):**
 * - Triggered for all other aggregation types (`avg`, `min`, `max`, `count`) OR if no formulas exist for the current level.
 * - Replicates standard SoQL generation logic, including special handling for `checkbox` (boolean casting) and `url` types.
 * - Supports `nonStandardAggregations` for custom-defined aggregation types.
 *
 * @param {ColumnVO[]} valueCols - The columns to be aggregated (e.g., `[{ id: 'sales', aggFunc: 'sum' }]`).
 * @param {ViewColumn[]} columns - Metadata for all available columns (used to determine data types like checkbox/url).
 * @param {HierarchyColumnConfig[]} configs - The hierarchy configuration containing `customFormula` definitions for each level.
 * @param {number} startLevelIndex - The index in `configs` to start processing from.
 * - Pass `groupKeys.length` to apply formulas for the *current* level (filtering the rows themselves).
 * - Pass `groupKeys.length + 1` to apply formulas for the *child* level (bubbling logic up to the parent group).
 * @param {ColumnAggregation[] | null} [nonStandardAggregations] - Optional definitions for custom aggregation types not natively supported by SoQL builder.
 *
 * @returns {NamedSelect[]} An array of SoQL SELECT objects ready for the query builder.
 *
 * @example
 * // =========================================================================
 * // SCENARIO 1: Single Level Formula (Simple Sum)
 * // =========================================================================
 * // Config: Country column has formula: "`US` + `CA`"
 * // Request: SUM('sales')
 * //
 * // Output SoQL:
 * // (
 * //   sum(case(`country` = 'US', `sales`, true, 0)) +
 * //   sum(case(`country` = 'CA', `sales`, true, 0))
 * // ) as __measure_alias__
 *
 * @example
 * // =========================================================================
 * // SCENARIO 2: Multi-Level Recursive
 * // =========================================================================
 * // Context: We are calculating the Grand Total (Level 0).
 * // Level 0 Config (Country): Formula is "`NorthAM`" (acting as a wrapper/alias).
 * // Level 1 Config (State):   Formula is "`NY` - `NJ`".
 * // Request: SUM('revenue')
 * //
 * // Logic:
 * // 1. Matches `NorthAM` token in Country formula. Generates `country`='NorthAM'.
 * // 2. Recurses to State config. Matches `NY` and `NJ`.
 * // 3. Injects Parent condition (`country`='NorthAM') into Child conditions.
 * //
 * // Output SoQL:
 * // (
 * //   (
 * //     sum(case(`country` = 'NorthAM' AND `state` = 'NY', `revenue`, true, 0)) -
 * //     sum(case(`country` = 'NorthAM' AND `state` = 'NJ', `revenue`, true, 0))
 * //   )
 * // ) as __measure_alias__
 *
 * @example
 * // =========================================================================
 * // SCENARIO 3: Multiple Aggregations (Mixed Types)
 * // =========================================================================
 * // Config: Country = "`US` + `UK`"
 * // Request:
 * //   1. SUM('sales')  -> Should use Advanced Totaling formula.
 * //   2. AVG('profit') -> Should use Standard SoQL (cannot sum averages arithmetically).
 * //
 * // Output SoQL (Array of 2 Selects):
 * // [
 * //   1. (sum(case(`country`='US', `sales`...)) + sum(case(`country`='UK', `sales`...))) as alias_0,
 * //   2. avg(`profit`) as alias_1
 * // ]
 *
 * @example
 * // =========================================================================
 * // SCENARIO 4: Arithmetic & Constants
 * // =========================================================================
 * // Config: Region = "(`East` * 1.5) + `West`"
 * // Request: SUM('goals')
 * //
 * // Logic: The recursion preserves the string literals (* 1.5, +) found in the formula.
 * //
 * // Output SoQL:
 * // (
 * //   (sum(case(`region` = 'East', `goals`, true, 0)) * 1.5) +
 * //   sum(case(`region` = 'West', `goals`, true, 0))
 * // ) as __measure_alias__
 *
 */
export function getAdvancedTotalingAggregations(
  valueCols: ColumnVO[],
  columns: ViewColumn[],
  configs: HierarchyColumnConfig[],
  startLevelIndex: number,
  nonStandardAggregations?: ColumnAggregation[] | null,
): NamedSelect[] {
  // Only process configs from the specified start level downwards
  const activeConfigs = configs.slice(startLevelIndex).filter((c) => c.isGrouping && !!c.customFormula);

  return valueCols
    .filter((col) => !!col.aggFunc)
    .map((col, idx) => {
      const viewCol = findCorrespondingColumn(columns, { id: col.id });
      if (!viewCol) return null;

      // -----------------------------------------------------------------------
      // PATH A: ADVANCED TOTALING (SUM Only)
      // -----------------------------------------------------------------------
      // We only apply the logic if it's a SUM and we have formulas to apply.
      if (col.aggFunc === 'sum' && activeConfigs.length > 0) {
        const complexExpression = buildNestedExpression(0, viewCol, col.aggFunc, activeConfigs);
        return prerenderedSelect(complexExpression, measureAlias(idx));
      }

      // -----------------------------------------------------------------------
      // PATH B: STANDARD FALLBACK (Everything else)
      // This logic replicates getSelectAggregationsQuery to support AVG, MIN, MAX, etc.
      // -----------------------------------------------------------------------

      // @ts-expect-error implicit any index
      if (typeof f[col.aggFunc] === 'function') {
        // Postgresql does not allow for min or max of boolean columns,
        // so we need to use this work around.
        if (viewCol?.dataTypeName === 'checkbox' && (col.aggFunc === 'max' || col.aggFunc === 'min')) {
          return select(f.eq(f[col.aggFunc](f.iif(viewCol, 1, 0)), 1), measureAlias(idx));
        } else if (viewCol?.dataTypeName === 'url' && (col.aggFunc === 'max' || col.aggFunc === 'min')) {
          return select(f.url(f[col.aggFunc](f.url_url(viewCol)), null), measureAlias(idx));
        }

        // @ts-expect-error implicit any index
        return select(f[col.aggFunc](viewCol), measureAlias(idx));
      } else if (nonStandardAggregations) {
        // in the case of a non-standard aggregation, we need to use the nonStandardAggregations
        // list to find the correct SoQL expression for this column/aggregationType
        const aggregationDefinition = nonStandardAggregations.find(
          (agg) => agg.fieldName === col.id && agg.aggregationType === col.aggFunc,
        );
        if (aggregationDefinition) {
          return prerenderedSelect(aggregationDefinition.soqlExpression, measureAlias(idx));
        }
      }

      return null;
    })
    .filter((s): s is NamedSelect => !!s);
}

/**
 *
 * @param {ViewColumn []} columns - All of the columns being used in the table visualization.
 * @param {string} legacyWhereClause - The output of SoqlHelpers.whereClauseFilteringOwnColumn
 * @param {Object} agRequest - The data request information from Ag Grid.
 * @param {string} searchString - An optional search string used on Primer
 * @returns - a query to be used in the Soql Query Builder, invokeSoql function.
 */
export function tableDataQuery(
  columns: ViewColumn[],
  legacyWhereClause: string,
  agRequest: IServerSideGetRowsRequest,
  pageSize?: number,
  startIndex?: number,
  order?: OrderConfig[],
  nonStandardAggregations?: ColumnAggregation[] | null,
  searchString?: string,
  customSortConfig?: CustomSortConfig,
  hierarchyConfigs?: HierarchyColumnConfig[],
) {
  const { groupKeys: agGroupKeys, rowGroupCols, pivotCols, valueCols } = agRequest;
  const groupKeys = removeNullGroupKeys(agGroupKeys);
  const isPivoting = isPivotInAgRequest(agRequest);
  const selectQuery = () => {
    const selectQueries: NamedSelect[] = [];
    const selectAliasMap: { [key: string]: string } = createSelectAliasMap(agRequest, columns);

    // select both the pivotColumn and groupColumn, if they exist
    if (isPivoting) {
      pivotCols.forEach((pivotCol) => {
        const pivotColId = findCorrespondingColumn(columns, pivotCol);
        if (typeof pivotColId !== 'undefined') {
          selectQueries.push(select(pivotColId, selectAliasMap[pivotCol.id]));
        }
      });
    }

    if (isDoingGrouping(rowGroupCols, groupKeys)) {
      const selectCol = rowGroupCols[groupKeys.length];
      const colId = findCorrespondingColumn(columns, selectCol);
      if (typeof colId !== 'undefined') {
        selectQueries.push(select(colId, selectAliasMap[selectCol.id]));
      }
    }

    // We must be pivoting or grouping, so there must be an aggregation.
    if (selectQueries.length > 0) {
      let aggregationSelects: NamedSelect[] = [];

      if (isTableAdvancedTotalingEnabled() && hierarchyConfigs) {
        // NOTE: We pass `groupKeys.length + 1` here.
        // Logic: If we are at level 0 (Top Rows), we want to apply the formulas from Level 1 (Children).
        // This ensures the custom formula bubbles UP to the group row, rather than filtering the group row itself.
        const startLevelIndex = groupKeys.length + 1;

        aggregationSelects = getAdvancedTotalingAggregations(
          valueCols,
          columns,
          hierarchyConfigs,
          startLevelIndex,
          nonStandardAggregations,
        );
      }

      if (aggregationSelects.length === 0) {
        aggregationSelects = getSelectAggregationsQuery(valueCols, columns, nonStandardAggregations);
      }

      selectQueries.push(...aggregationSelects);
      return selectQueries;
    }

    // We didn't do any grouping or pivoting, so we must be at the lowest level.
    // So just select all the columns we've added to the table
    const ungroupedColumns = differenceWith(columns, rowGroupCols, (c, r) => c.fieldName === r.id);
    each(ungroupedColumns, (col) => {
      selectQueries.push(select(col, selectAliasMap[col.fieldName]));
    });

    return selectQueries;
  };

  const groupQuery = () => {
    const groupQueries: ViewColumn[] = [];

    // group by both pivot and grouping columns, if they exist
    // so would look like `GROUP BY pivotColumn, groupColumn`
    if (isPivoting) {
      pivotCols.forEach((pivotCol) => {
        const pivotId = findCorrespondingColumn(columns, pivotCol);
        if (typeof pivotId !== 'undefined') {
          groupQueries.push(pivotId);
        }
      });
    }

    if (isDoingGrouping(rowGroupCols, groupKeys)) {
      const rowGroupCol = rowGroupCols[groupKeys.length];
      const groupId = findCorrespondingColumn(columns, rowGroupCol);
      if (typeof groupId !== 'undefined') {
        groupQueries.push(groupId);
      }
    }

    return groupQueries;
  };

  const orderByQuery = () => {
    if (isUndefined(order)) {
      return [];
    }
    const rowGroupCol = rowGroupCols[groupKeys.length];

    return order
      .map((o: OrderConfig) => {
        const col = find(
          columns,
          (c) => c.fieldName === replace(o.columnName, `${GROUP_COLUMN_PREFIX}-`, ''),
        );
        const ordering = o.ascending === true ? 'ascending' : 'descending';

        if (isUndefined(col) || col.fieldName === ':id') {
          return;
        }

        if (isDoingGrouping(rowGroupCols, groupKeys)) {
          // Table with groupings
          if (col.fieldName === rowGroupCol.id) {
            // OrderBy: Grouping column
            return orderBy(col, ordering);
          } else {
            const measureAliasMap = createMeasureAliasMap(agRequest);
            const measureAliasKey = findKey(measureAliasMap, (fieldName) => fieldName === col.fieldName);
            if (isUndefined(measureAliasKey)) {
              // OrderBy: Non Aggregated/Grouping Column, so ignored
              return;
            }
            // OrderBy: Aggregated column
            return orderBy(measureAliasKey, ordering);
          }
        } else {
          // Table without groupings
          return orderBy(col, ordering);
        }
      })
      .filter((orderForSoql) => !isUndefined(orderForSoql));
  };

  const groupedColumn = groupQuery();
  const whereParts = generateWhereQuery(columns, groupKeys, rowGroupCols);
  const selectParts = selectQuery();
  const orderParts = orderByQuery();
  const searchParts = searchString;

  const params = {
    selects: selectParts,
    where: whereParts,
    groups: groupedColumn,
    legacyWhereClause,
    orders: orderParts,
    limit: pageSize,
    offset: startIndex,
    search: searchParts,
    ...(isCustomSortEnabled() &&
      !isEmpty(customSortConfig) && {
        cases: buildCasesParts(selectParts, customSortConfig, order),
      }),
  };
  const dataQuery = composeQuery(params);
  return dataQuery;
}
